import { computed, ref } from 'vue'

import { useModal } from '@shared/composables/use-modal'
import { Namespaces } from '@shared/models/enums/namespaces'
import { IGetNotificationsRO } from '@shared/models/proto/base/notifications'
import { useNotificationsStore } from '@shared/store/web-notifications'
import { useFetchData } from '@shared/utils/composables/fetch-data'
import { INITIAL_PER_PAGE_VARIANTS } from '@shared/utils/constants/pagination'
import { createSharedComposable } from '@shared/utils/helpers/create-composable'

export const useWebNotifications = createSharedComposable(
  (params: { namespace: Namespaces }) => {
    const { namespace } = params

    const { fetchData, isLoading, isSuccess } =
      useFetchData<IGetNotificationsRO>()

    const {
      fetchData: clearData,
      isLoading: isLoadingClear,
      loadingDataId,
    } = useFetchData()

    const { isVisible, setVisible } = useModal()

    const notificationsStore = useNotificationsStore(namespace)

    const pagination = ref({
      page: 1,
      perPage: INITIAL_PER_PAGE_VARIANTS[3],
    })

    const list = computed(() => notificationsStore.list)

    const meta = computed(() => notificationsStore.meta)

    const isDisabledScroll = computed(
      () => isLoading.value || meta.value?.totalPages === meta.value?.page
    )

    const createWebNotificationsChannel = () =>
      notificationsStore.createNotificationChannel()

    const closeWebNotificationsChannel = () =>
      notificationsStore.closeNotificationChannel()

    const readNotification = (notificationId: number) => {
      clearData({
        onFetchDataCallback: async () => {
          await notificationsStore.setNotificationAsReaded(notificationId)
        },
        loadingDataId: notificationId,
      })
    }

    const scrollHandler = () => {
      pagination.value.page += 1
      fetchNotificationData()
    }

    const openDrawer = () => {
      setVisible(true)

      fetchNotificationData()
    }

    const fetchNotificationData = () => {
      fetchData({
        onFetchDataCallback: async () => {
          return await notificationsStore.fetchNotifications(
            pagination.value,
            isSuccess.value
          )
        },
      })
    }

    return {
      setVisible,
      openDrawer,
      fetchNotificationData,
      scrollHandler,
      readNotification,
      createWebNotificationsChannel,
      closeWebNotificationsChannel,
      isLoading,
      isLoadingClear,
      loadingDataId,
      isVisible,
      isDisabledScroll,
      list,
    }
  }
)
